'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { PureComponent, Fragment } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { NavMarker } from 'react-rhumb';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import text from 'I18n/utils/unescapedText';
import UIScrollContainer from 'UIComponents/scroll/UIScrollContainer';
import UIButton from 'UIComponents/button/UIButton';
import UIErrorMessage from 'UIComponents/error/UIErrorMessage';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIPaginator from 'UIComponents/paginator/UIPaginator';
import * as FilesActions from 'DesignData/actions/finder/Files';
import { push } from 'DesignData/actions/History';
import * as RequestStatus from 'DesignData/enums/RequestStatus';
import { KEYS, MODIFIERS } from 'DesignData/enums/Keys';
import { createRevisionPath } from 'DesignData/helpers/routingHelper';
import Keybinding from 'DesignData/models/Keybinding';
import RevisionModel from 'DesignData/models/revisions/Revision';
import { FilePropTypes } from 'DesignData/propTypes/finder';
import { DEFAULT_PAGINATION_LIMIT } from 'DesignData/settings/api';
import withKeyboardShortcuts from 'DesignComponents/decorators/withKeyboardShortcuts';
import { CURRENT_REVISION_ID } from 'DesignData/constants/Revisions';
import { tryParseNumber } from 'DesignData/lib/text';
import * as appSelectors from 'DesignData/selectors/app';
import { getRevisionsData, getIsUpgradedModule, getBufferForRevision } from 'DesignData/selectors/revisions';
import { getFile, getIsInLockedFolderFactory } from 'DesignData/selectors/finder';
import { getLastPublishedAt } from 'DesignData/selectors/session';
import FileResults from 'DesignFinderUI/components/results/FileResults';
import FileResultsEmptyText from 'DesignFinderUI/components/results/FileResultsEmptyText';
import FileResultsList from 'DesignFinderUI/components/results/FileResultsList';
import RevisionResultItem from 'DesignFinderUI/components/results/revisions/RevisionResultItem';
import FinderWrapper from 'DesignFinderUI/containers/FinderWrapper';
import { compose } from 'redux';
const FileResultsWithKeyboardShortcuts = withKeyboardShortcuts()(FileResults);

const i18nKey = key => `designManager.finder.FileRevisions.${key}`;

function makeMapStateToProps() {
  return function mapStateToProps(state, props) {
    const assetId = appSelectors.getActiveAssetId(state);
    const activeRevisionId = tryParseNumber(props.match.params.revisionId) || CURRENT_REVISION_ID;
    const file = getFile(state, {
      fileId: assetId
    });
    const getIsInLockedFolder = getIsInLockedFolderFactory(state);
    const isLocked = getIsInLockedFolder(file && file.folderId);
    const {
      revisions,
      order = [],
      fetchStatus,
      fetchError,
      total: revisionsCount
    } = getRevisionsData(state, {
      assetId
    });
    const fileAssetType = file && file.designAssetType;
    const isUpgradedModule = getIsUpgradedModule(state, {
      fileId: assetId
    });
    const buffer = fileAssetType && getBufferForRevision(state, {
      fileAssetType,
      assetId
    });
    return {
      fileId: assetId,
      fileAssetType,
      fileName: file && file.name,
      isFetchingFile: !file,
      isUpgradedModule,
      revisions,
      revisionsCount,
      activeRevisionIndex: order.indexOf(activeRevisionId),
      activeRevisionId,
      activeAssetId: appSelectors.getActiveAssetId(state),
      fetchStatus,
      fetchError,
      bufferTimestamp: buffer && buffer.updated || 0,
      lastPublishedAt: getLastPublishedAt(state, {
        assetId
      }),
      isLocked
    };
  };
}

const mapDispatchToProps = {
  fetchRevisions: FilesActions.fetchRevisions,
  revertFile: FilesActions.revertFile,
  hideRevisions: FilesActions.hideRevisions
};

class FileRevisions extends PureComponent {
  constructor(...args) {
    super(...args);
    this.state = {
      offset: 0
    };

    this.getFileRevisionRoute = _revisionId => {
      const {
        fileId,
        fileAssetType
      } = this.props;
      const revisionId = _revisionId !== CURRENT_REVISION_ID && _revisionId;
      return createRevisionPath(fileAssetType, fileId, revisionId);
    };

    this.handleClose = () => {
      this.props.hideRevisions(this.props.fileId);
    };

    this.handlePageChange = evt => {
      const pageNumber = evt.target.value;
      const newOffset = pageNumber * DEFAULT_PAGINATION_LIMIT - DEFAULT_PAGINATION_LIMIT;

      if (newOffset !== this.state.offset) {
        this.setState({
          offset: newOffset
        });
      }
    };

    this.handleFetchRevisions = (offset = this.state.offset) => {
      this.props.fetchRevisions(this.props.fileId, offset);
    };

    this.handleNavigateToNextRevision = () => {
      const {
        activeRevisionIndex,
        revisions
      } = this.props;
      const nextRevision = revisions[activeRevisionIndex + 1];

      if (nextRevision) {
        this.handleNavigateToRevision(nextRevision.id);
      }
    };

    this.handleNavigateToPreviousRevision = () => {
      const {
        activeRevisionIndex,
        revisions
      } = this.props;
      const previousRevision = revisions[activeRevisionIndex - 1];

      if (previousRevision) {
        this.handleNavigateToRevision(previousRevision.id);
      }
    };

    this.keybindingConfig = {
      [MODIFIERS.NONE]: {
        [KEYS.UP_ARROW]: new Keybinding({
          handler: this.handleNavigateToPreviousRevision
        }),
        [KEYS.DOWN_ARROW]: new Keybinding({
          handler: this.handleNavigateToNextRevision
        })
      }
    };

    this.handleNavigateToRevision = revisionId => {
      const revisionRoute = this.getFileRevisionRoute(revisionId);
      push(revisionRoute);
    };

    this.handleRevert = revisionId => {
      const {
        fileId,
        revertFile,
        revisions
      } = this.props;
      const revision = revisions.find(__revision => __revision.id === revisionId);
      revertFile(fileId, revision);
    };
  }

  componentDidMount() {
    if (!this.props.isFetchingFile) this.handleFetchRevisions();
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      offset
    } = this.state;
    const {
      isFetchingFile,
      fileId,
      lastPublishedAt
    } = this.props;

    if (prevState.offset !== offset) {
      this.handleFetchRevisions(offset);
    } else if (prevProps.isFetchingFile && !isFetchingFile || prevProps.fileId && !fileId || prevProps.fileId !== fileId && !isFetchingFile || prevProps.lastPublishedAt !== lastPublishedAt) {
      this.handleFetchRevisions();
    }
  }

  get isFetching() {
    return this.props.fetchStatus === RequestStatus.PENDING || this.props.isFetchingFile;
  }

  renderRevisions() {
    const {
      activeRevisionId,
      fetchStatus,
      fileName,
      revisions,
      revisionsCount,
      isUpgradedModule,
      bufferTimestamp,
      isLocked
    } = this.props;

    if (fetchStatus === RequestStatus.FAILED) {
      return /*#__PURE__*/_jsx(NavMarker, {
        name: "FINDER_REVISIONS_ERROR",
        children: /*#__PURE__*/_jsx(FileResultsEmptyText, {
          children: /*#__PURE__*/_jsxs(UIErrorMessage, {
            illustrationProps: {
              width: 0
            },
            title: text(i18nKey('errorMessage.title')),
            children: [/*#__PURE__*/_jsx("p", {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: i18nKey('errorMessage.message'),
                options: {
                  fileName: I18n.SafeString(fileName)
                }
              })
            }), /*#__PURE__*/_jsxs(UIButton, {
              onClick: () => this.handleFetchRevisions(),
              use: "link",
              children: [/*#__PURE__*/_jsx(UIIcon, {
                name: "refresh"
              }), /*#__PURE__*/_jsx(FormattedMessage, {
                message: i18nKey('errorMessage.retryButtonText')
              })]
            })]
          })
        })
      });
    }

    const {
      offset
    } = this.state;
    const revisionItems = revisions.map((revision, revisionIndex) => {
      const {
        id,
        object,
        user,
        updatedAt
      } = revision;
      const isActive = id === activeRevisionId;
      const isCurrent = id === CURRENT_REVISION_ID;
      const revisionNumber = revisionsCount - (revisionIndex + offset);
      const revisionRoute = this.getFileRevisionRoute(id);
      const isBeforeUpgrade = isUpgradedModule && object.schemaVersion === 1;
      const revertDisabled = isLocked || isBeforeUpgrade;
      let revertButtonTooltip = null;

      if (revertDisabled && isLocked) {
        revertButtonTooltip = /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('revertDisabledTooltip.isLocked')
        });
      } else if (revertDisabled) {
        revertButtonTooltip = /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('revertDisabledTooltip.upgradedModule')
        });
      }

      return /*#__PURE__*/_jsx(RevisionResultItem, {
        active: isActive,
        isCurrent: isCurrent,
        revertDisabled: revertDisabled,
        revertButtonTooltip: revertButtonTooltip,
        onNavigateToRevision: this.handleNavigateToRevision,
        onRevertClick: this.handleRevert,
        revisionId: id,
        revisionNumber: revisionNumber,
        revisionRoute: revisionRoute,
        updated: isCurrent ? bufferTimestamp : updatedAt,
        user: user
      }, id);
    });
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(NavMarker, {
        name: "FINDER_REVISIONS"
      }), /*#__PURE__*/_jsx(UIScrollContainer, {
        className: "file-results__scroll-section",
        children: /*#__PURE__*/_jsx(FileResultsList, {
          ordered: true,
          reversed: true,
          start: revisionsCount,
          isFetching: this.isFetching,
          total: revisionsCount,
          children: revisionItems
        })
      })]
    });
  }

  renderPaginator() {
    const {
      revisionsCount
    } = this.props;
    const {
      offset
    } = this.state;

    if (!revisionsCount) {
      return null;
    }

    const pageNumber = (offset + DEFAULT_PAGINATION_LIMIT) / DEFAULT_PAGINATION_LIMIT;
    const pageCount = Math.ceil(revisionsCount / DEFAULT_PAGINATION_LIMIT);
    return /*#__PURE__*/_jsx(UIPaginator, {
      className: "p-y-5",
      maxVisiblePageButtons: 4,
      onPageChange: this.handlePageChange,
      page: pageNumber,
      pageCount: pageCount
    });
  }

  render() {
    const {
      fileName,
      revisionsCount
    } = this.props;
    const headerText = text(i18nKey('header'), {
      count: revisionsCount,
      fileName: I18n.SafeString(fileName)
    });
    const renderedPaginator = this.renderPaginator();
    return /*#__PURE__*/_jsx(FinderWrapper, {
      className: "file-revisions-container",
      children: /*#__PURE__*/_jsx(FileResultsWithKeyboardShortcuts, Object.assign({}, this.props, {
        footer: renderedPaginator,
        headerText: headerText,
        isFetching: this.isFetching,
        keybindingConfig: this.keybindingConfig,
        onClose: this.handleClose,
        children: this.renderRevisions()
      }))
    });
  }

}

FileRevisions.propTypes = {
  activeRevisionId: PropTypes.number,
  activeRevisionIndex: PropTypes.number,
  fetchRevisions: PropTypes.func.isRequired,
  fetchStatus: PropTypes.oneOf(Object.values(RequestStatus || {})),
  fileAssetType: FilePropTypes.fileAssetType.isRequired,
  fileId: FilePropTypes.fileId,
  fileName: FilePropTypes.fileName.isRequired,
  hideRevisions: PropTypes.func.isRequired,
  isFetchingFile: PropTypes.bool.isRequired,
  isLocked: PropTypes.bool.isRequired,
  isUpgradedModule: PropTypes.bool.isRequired,
  revertFile: PropTypes.func.isRequired,
  revisions: PropTypes.arrayOf(PropTypes.instanceOf(RevisionModel)),
  revisionsCount: PropTypes.number,
  bufferTimestamp: PropTypes.number.isRequired,
  lastPublishedAt: PropTypes.number
};
FileRevisions.defaultProps = {
  activeRevisionId: CURRENT_REVISION_ID,
  activeRevisionIndex: -1,
  fetchStatus: null,
  revisions: [],
  revisionsCount: 0,
  fileName: '',
  fileAssetType: 0,
  isFetchingFile: true,
  isUpgradedModule: false
};
export default compose(withRouter, connect(makeMapStateToProps, mapDispatchToProps))(FileRevisions);